import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const ImageFlipBook = ({ images = [] }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const containerRef = useRef(null);
  const pageWidth = useRef(typeof window !== 'undefined' ? window.innerWidth : 1000);
  const scrollBlocked = useRef(false);

  useEffect(() => {
    const updateWidth = () => { pageWidth.current = window.innerWidth; };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const goToPage = useCallback((page) => {
    if (!containerRef.current || scrollBlocked.current) return;
    scrollBlocked.current = true;
    containerRef.current.scrollTo({ left: page * pageWidth.current, behavior: 'instant' });
    setCurrentPage(page);
    setTimeout(() => { scrollBlocked.current = false; }, 300);
  }, []);

  const handlePrev = useCallback(() => {
    if (currentPage > 0) goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const handleNext = useCallback(() => {
    if (currentPage < images.length - 1) goToPage(currentPage + 1);
  }, [currentPage, images.length, goToPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const page = Math.round(containerRef.current.scrollLeft / pageWidth.current);
      if (page !== currentPage && page >= 0 && page < images.length) {
        setCurrentPage(page);
      }
    };
    const container = containerRef.current;
    container?.addEventListener('scroll', handleScroll, { passive: true });
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [currentPage, images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePrev, handleNext]);

  useEffect(() => {
    const onWheel = (e) => {
      if (!scrollBlocked.current && Math.abs(e.deltaX) > 10) {
        e.preventDefault();
        if (e.deltaX > 0) handleNext();
        else handlePrev();
        scrollBlocked.current = true;
        setTimeout(() => { scrollBlocked.current = false; }, 300);
      }
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [handlePrev, handleNext]);

  return (
    <div className="fixed inset-0 bg-mainBlue">
      <div
        ref={containerRef}
        className="w-full h-full flex overflow-x-auto overflow-y-hidden"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {images.map((src, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-screen h-screen flex items-center justify-center p-4 md:p-8"
            style={{ scrollSnapAlign: 'center' }}
          >
            <img
              src={src}
              alt={`Page ${idx + 1}`}
              className="max-w-full max-h-full object-contain rounded shadow-xl"
              draggable={false}
            />
          </div>
        ))}
      </div>
      
      <button
        onClick={handlePrev}
        disabled={currentPage === 0}
        className="fixed left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white p-2 md:p-3 bg-black/40 rounded-full disabled:opacity-0"
      >
        <FaChevronLeft size={20} className="md:text-2xl" />
      </button>
      <button
        onClick={handleNext}
        disabled={currentPage === images.length - 1}
        className="fixed right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white p-2 md:p-3 bg-black/40 rounded-full disabled:opacity-0"
      >
        <FaChevronRight size={20} className="md:text-2xl" />
      </button>
      
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex gap-1 md:gap-2 z-20">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToPage(idx)}
            className={`h-1.5 md:h-2 rounded-full transition-all ${
              currentPage === idx ? 'bg-white w-4 md:w-6' : 'bg-white/40 w-1.5 md:w-2 hover:bg-white/60'
            }`}
          />
        ))}
      </div>
      <div className="fixed bottom-1 md:bottom-2 left-1/2 -translate-x-1/2 text-white/60 text-xs md:text-sm z-20">
        {currentPage + 1} / {images.length}
      </div>
    </div>
  );
};

export default ImageFlipBook;